import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import ScenarioRunner from "../components/studio/ScenarioRunner";
import StudioPreviewCard from "../components/studio/preview/StudioPreviewCard";
import StudioPreviewStatusPill from "../components/studio/preview/StudioPreviewStatusPill";
import {
  getStudioDemoLauncherPreset,
  isStudioDemoLauncherId,
  type StudioDemoLauncherId,
} from "../lib/studio/studioDemoLauncher";
import {
  loadStudioPreviewCenterState,
  runStudioTextPreview,
  type StudioPreviewCenterState,
  type StudioLaunchControlState,
  type StudioTextPreviewChannel,
  type StudioTextPreviewResult,
  type StudioVoiceQualityReadinessState,
  type StudioVoicePreviewLane,
} from "../lib/studio/studioPreviewCenter";
import {
  appendStudioPreviewEvidence,
  createStudioTextPreviewEvidenceEntry,
  loadStudioPreviewEvidenceLedger,
  type StudioPreviewEvidenceEntry,
} from "../lib/studio/studioPreviewEvidenceLedger";
import {
  loadStudioWorkspaceStorage,
} from "../lib/studio/studioWorkspaceStorage";
import {
  getStudioApprovedVoiceOption,
  getStudioDefaultApprovedElevenLabsVoice,
  getStudioWorkspaceVoicePersona,
  type StudioApprovedVoiceOptionId,
  type StudioWorkspaceVoicePersonaId,
} from "../lib/studio/studioWorkspaceState";
import {
  getStudioScenarioPreset,
  isStudioScenarioPresetId,
  type StudioScenarioPresetId,
} from "../lib/studio/scenarioPresets";
import { VETCAN_STUDIO_STEPS, type VetCanStudioStepId } from "../lib/studio/studioReadModel";

function detailLabel(label: string, value: string) {
  return (
    <div
      style={{
        borderRadius: 18,
        border: "1px solid rgba(54, 44, 35, 0.08)",
        background: "#fbf8f4",
        padding: "0.9rem",
      }}
    >
      <div
        style={{
          fontSize: "0.7rem",
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: "#85796e",
          fontWeight: 700,
        }}
      >
        {label}
      </div>
      <div
        style={{
          marginTop: "0.38rem",
          color: "#26221d",
          fontWeight: 700,
          lineHeight: 1.45,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function storyCard(label: string, value: string, detail: string) {
  return (
    <div
      style={{
        borderRadius: 18,
        border: "1px solid rgba(247, 244, 238, 0.16)",
        background: "rgba(247, 244, 238, 0.1)",
        padding: "0.9rem",
      }}
    >
      <div
        style={{
          fontSize: "0.72rem",
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: "rgba(247, 244, 238, 0.72)",
          fontWeight: 700,
        }}
      >
        {label}
      </div>
      <div style={{ marginTop: "0.35rem", fontSize: "1rem", fontWeight: 700 }}>
        {value}
      </div>
      <p
        style={{
          margin: "0.35rem 0 0",
          color: "rgba(247, 244, 238, 0.84)",
          lineHeight: 1.55,
          fontSize: "0.86rem",
        }}
      >
        {detail}
      </p>
    </div>
  );
}

function voiceSourceLabel(voiceLane: StudioVoicePreviewLane | null) {
  if (!voiceLane) return "Unavailable";
  if (voiceLane.personaSelection.voiceIdSource === "elevenlabs_preview_voice_id") {
    return "Configured via ELEVENLABS_PREVIEW_VOICE_ID";
  }

  if (voiceLane.personaSelection.voiceIdSource === "elevenlabs_voice_id") {
    return "Configured via ELEVENLABS_VOICE_ID";
  }

  return "Missing";
}

function bindingResolutionLabel(voiceLane: StudioVoicePreviewLane | null) {
  if (!voiceLane) return "Unavailable";
  if (voiceLane.personaSelection.bindingResolution === "studio_catalog") {
    return "Mapped from Studio preview catalog";
  }

  if (voiceLane.personaSelection.fallbackReason === "studio_persona_unmapped") {
    return "Draft persona not mapped; using server preview env default";
  }

  return "Server preview env default";
}

function activeRuntimeVoiceLabel(voiceLane: StudioVoicePreviewLane | null) {
  if (!voiceLane) return "Unavailable";
  return voiceLane.personaSelection.activeVoiceLabel || voiceLane.personaSelection.personaName;
}

function launchControlTone(launchControl: StudioLaunchControlState | null) {
  if (!launchControl) return "blocked";
  return launchControl.overallReady ? "ready" : "blocked";
}

function formatEvidenceTimestamp(recordedAt: string | null) {
  if (!recordedAt) {
    return "No recorded preview evidence yet.";
  }

  const timestamp = new Date(recordedAt);
  if (Number.isNaN(timestamp.getTime())) {
    return "Recorded preview evidence available.";
  }

  return `Latest evidence ${new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(timestamp)}`;
}

function blockedPreviewCenterState(): StudioPreviewCenterState {
  return {
    access: {
      status: "blocked",
      reason: "auth_required",
    },
    textPreview: {
      supportedChannels: ["chat", "voice", "outbound_voice"],
      mode: "internal_only",
    },
    voiceReadiness: null,
    voiceQualityReadiness: null,
    deploymentTruth: null,
    errors: {
      voiceReadiness: null,
      voiceQualityReadiness: null,
      deploymentTruth: null,
    },
  };
}

function voiceFallbackReasonLabel(
  voiceQualityReadiness: StudioVoiceQualityReadinessState | null
) {
  const fallbackReason = voiceQualityReadiness?.fallbackReason;
  if (!fallbackReason) return "No fallback reported";
  return fallbackReason.replaceAll("_", " ");
}

function getRequestedScenarioPresetId() {
  if (typeof window === "undefined") {
    return null;
  }

  const requestedScenario = new URLSearchParams(window.location.search).get("scenario");
  if (!isStudioScenarioPresetId(requestedScenario)) {
    return null;
  }

  return getStudioScenarioPreset(requestedScenario).id;
}

function getRequestedDemoId() {
  if (typeof window === "undefined") {
    return null;
  }

  const requestedDemo = new URLSearchParams(window.location.search).get("demo");
  return isStudioDemoLauncherId(requestedDemo) ? requestedDemo : null;
}

function getStudioReturnHref() {
  if (typeof window === "undefined") {
    return "/studio?step=preview";
  }

  const params = new URLSearchParams(window.location.search);
  const requestedStep = params.get("returnStep");
  const requestedDemo = params.get("demo");
  const validStep = VETCAN_STUDIO_STEPS.includes(requestedStep as VetCanStudioStepId)
    ? requestedStep
    : "preview";
  const nextSearch = new URLSearchParams();
  nextSearch.set("step", validStep);

  if (isStudioDemoLauncherId(requestedDemo)) {
    nextSearch.set("demo", requestedDemo);
  }

  return `/studio?${nextSearch.toString()}`;
}

export default function VetCanStudioPreview() {
  const [loading, setLoading] = useState(true);
  const [centerState, setCenterState] = useState<StudioPreviewCenterState | null>(null);
  const [selectedStudioPersonaId, setSelectedStudioPersonaId] =
    useState<StudioWorkspaceVoicePersonaId>(() => {
      return loadStudioWorkspaceStorage().state.voicePersona.selectedPersonaId;
    });
  const [selectedApprovedVoiceId, setSelectedApprovedVoiceId] =
    useState<StudioApprovedVoiceOptionId>(() => {
      return loadStudioWorkspaceStorage().state.voicePersona.selectedVoiceOptionId;
    });
  const [evidenceEntries, setEvidenceEntries] = useState<StudioPreviewEvidenceEntry[]>(
    () => loadStudioPreviewEvidenceLedger().entries
  );

  const [textChannel, setTextChannel] = useState<StudioTextPreviewChannel>("chat");
  const [textMessage, setTextMessage] = useState("What are your hours?");
  const [textSubmitting, setTextSubmitting] = useState(false);
  const [textError, setTextError] = useState<string | null>(null);
  const [textResult, setTextResult] = useState<StudioTextPreviewResult | null>(null);

  async function refreshState() {
    setLoading(true);

    try {
      const workspaceState = loadStudioWorkspaceStorage().state;
      setSelectedStudioPersonaId(workspaceState.voicePersona.selectedPersonaId);
      setSelectedApprovedVoiceId(workspaceState.voicePersona.selectedVoiceOptionId);
      const state = await loadStudioPreviewCenterState({
        studioPersonaKey: workspaceState.voicePersona.selectedPersonaId,
      });
      setCenterState(state);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refreshState();
  }, []);

  const accessBlocked = centerState?.access.status === "blocked";
  const voiceLane = centerState?.voiceReadiness || null;
  const voiceQualityReadiness = centerState?.voiceQualityReadiness || null;
  const launchControl = centerState?.deploymentTruth || null;
  const stateErrors = centerState?.errors || {
    voiceReadiness: null,
    voiceQualityReadiness: null,
    deploymentTruth: null,
  };
  const voiceReady = Boolean(voiceLane?.ready) && !accessBlocked;
  const previewBlockers = useMemo(
    () => [...(voiceLane?.blockedReasons || [])],
    [voiceLane]
  );
  const textChannels = centerState?.textPreview.supportedChannels || [
    "chat",
    "voice",
    "outbound_voice",
  ];
  const studioReturnHref = getStudioReturnHref();
  const launchNextActions = useMemo(
    () =>
      (launchControl?.nextActions || [])
        .map((action) => action.action || action.category || "")
        .filter(Boolean),
    [launchControl]
  );
  const latestEvidence = evidenceEntries[0] || null;
  const requestedScenarioId = useMemo(() => getRequestedScenarioPresetId(), []);
  const requestedDemoId = useMemo(() => getRequestedDemoId(), []);
  const requestedDemo = requestedDemoId
    ? getStudioDemoLauncherPreset(requestedDemoId)
    : null;
  const selectedStudioPersona = getStudioWorkspaceVoicePersona(selectedStudioPersonaId);
  const selectedApprovedVoice = getStudioApprovedVoiceOption(selectedApprovedVoiceId);
  const currentRuntimeDefaultVoice = getStudioDefaultApprovedElevenLabsVoice();

  async function handleTextPreview(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const message = textMessage.trim();
    if (!message || accessBlocked) {
      setTextResult(null);
      setTextError(accessBlocked ? "Operator access is required." : "Enter a preview message.");
      return;
    }

    setTextSubmitting(true);
    setTextError(null);

    try {
      const { response, payload } = await runStudioTextPreview({
        channel: textChannel,
        message,
      });

      if (response.status === 401 || response.status === 403) {
        setCenterState((current) =>
          current
            ? {
                ...current,
                access: {
                  status: "blocked",
                  reason: "auth_required",
                },
              }
            : blockedPreviewCenterState()
        );
        setTextResult(null);
        setTextError("Operator access is required for internal preview.");
        return;
      }

      if (!response.ok) {
        setTextResult(null);
        setTextError("Unable to run text preview.");
        return;
      }

      setTextResult(payload as StudioTextPreviewResult);
      setTextError(null);
      const entry = appendStudioPreviewEvidence(
        createStudioTextPreviewEvidenceEntry({
          channel: textChannel,
          result: payload as StudioTextPreviewResult,
          voiceLane,
        })
      );
      if (entry) {
        setEvidenceEntries((current) => [entry, ...current].slice(0, 24));
      }
    } catch {
      setTextResult(null);
      setTextError("Unable to run text preview.");
    } finally {
      setTextSubmitting(false);
    }
  }

  return (
    <main
      style={{
        minHeight: "100vh",
        background:
          "radial-gradient(circle at top right, rgba(202, 224, 232, 0.42), transparent 30%), linear-gradient(180deg, #f7f1e8 0%, #fcfbf8 34%, #f2ece4 100%)",
        color: "#231f1a",
      }}
    >
      <div
        style={{
          maxWidth: 1440,
          margin: "0 auto",
          padding: "2rem 1.25rem 3rem",
        }}
      >
        <section
          style={{
            borderRadius: 34,
            padding: "1.5rem",
            background:
              "linear-gradient(135deg, rgba(20, 35, 42, 0.96), rgba(41, 84, 103, 0.92) 58%, rgba(209, 177, 129, 0.9) 124%)",
            color: "#f7f4ee",
            boxShadow: "0 34px 60px rgba(28, 39, 45, 0.22)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "1rem",
              flexWrap: "wrap",
              alignItems: "flex-start",
            }}
          >
            <div style={{ maxWidth: 760 }}>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.55rem",
                  padding: "0.38rem 0.8rem",
                  borderRadius: 999,
                  background: "rgba(247, 244, 238, 0.12)",
                  fontSize: "0.8rem",
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  fontWeight: 700,
                }}
              >
                Preview runtime review
              </div>
              <h1
                style={{
                  margin: "1rem 0 0.75rem",
                  fontSize: "clamp(2rem, 4vw, 3.4rem)",
                  lineHeight: 1,
                  fontFamily: "Georgia, 'Times New Roman', serif",
                  fontWeight: 700,
                }}
              >
                VetCan Studio Preview & Test Center
              </h1>
              <p
                style={{
                  margin: 0,
                  fontSize: "1.04rem",
                  lineHeight: 1.65,
                  color: "rgba(247, 244, 238, 0.9)",
                }}
              >
                Preview is the rehearsal room. Use it to test text, inspect the
                voice lane, and collect evidence without touching live clinic lanes.
              </p>
              <p
                style={{
                  margin: "0.75rem 0 0",
                  color: "rgba(247, 244, 238, 0.9)",
                  lineHeight: 1.65,
                }}
              >
                Studio holds the working draft. Preview shows the current preview
                runtime and evidence. The Dashboard remains the live read-only
                truth surface. Launch control here is read-only guidance, not a
                deployment button.
              </p>
              <p
                style={{
                  margin: "0.75rem 0 0",
                  color: "rgba(247, 244, 238, 0.82)",
                  lineHeight: 1.65,
                }}
              >
                In the first week after install, use the Dashboard and the
                week-one stabilization checklist for live callback, exception, and
                alert review. Use Preview only for staged review or non-live proof.
              </p>
              <div
                style={{
                  marginTop: "0.95rem",
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
                  gap: "0.75rem",
                }}
              >
                {storyCard(
                  "Studio",
                  "Working draft",
                  "Go back to Studio to change the draft, approvals, or handoff notes."
                )}
                {storyCard(
                  "Preview",
                  "Runtime rehearsal",
                  "Use this page to test safe preview behavior and capture evidence."
                )}
                {storyCard(
                  "Dashboard",
                  "Live read-only view",
                  "Use the Dashboard to check what is happening now in the live system."
                )}
                {storyCard(
                  "Launch control",
                  "Safety guidance",
                  "It tells you what still blocks live use. It does not make anything live."
                )}
              </div>
              {requestedDemo ? (
                <div
                  style={{
                    marginTop: "0.95rem",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.55rem",
                    borderRadius: 999,
                    padding: "0.38rem 0.82rem",
                    background: "rgba(247, 244, 238, 0.12)",
                    color: "#f7f4ee",
                    fontWeight: 700,
                  }}
                >
                  Demo launcher focus: {requestedDemo.label}
                </div>
              ) : null}
            </div>

            <div
              style={{
                display: "flex",
                gap: "0.75rem",
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <Link
                to={studioReturnHref}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 999,
                  padding: "0.82rem 1.15rem",
                  textDecoration: "none",
                  color: "#f7f4ee",
                  border: "1px solid rgba(247, 244, 238, 0.2)",
                  background: "rgba(247, 244, 238, 0.08)",
                  fontWeight: 700,
                }}
              >
                Back to Studio
              </Link>
              <Link
                to="/metrics"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 999,
                  padding: "0.82rem 1.15rem",
                  textDecoration: "none",
                  color: "#173847",
                  background: "#f7f4ee",
                  fontWeight: 700,
                }}
              >
                Open Ops Dashboard
              </Link>
              <button
                type="button"
                onClick={() => void refreshState()}
                style={{
                  borderRadius: 999,
                  padding: "0.82rem 1.15rem",
                  border: "1px solid rgba(247, 244, 238, 0.2)",
                  background: "rgba(10, 20, 26, 0.18)",
                  color: "#f7f4ee",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                {loading ? "Refreshing..." : "Refresh preview state"}
              </button>
            </div>
          </div>
        </section>

        <section
          style={{
            marginTop: "1.25rem",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "1rem",
          }}
        >
          <StudioPreviewCard title="Text preview" eyebrow="Current state">
            <StudioPreviewStatusPill
              status={textSubmitting ? "loading" : textResult ? "rendered" : "ready"}
              label={textSubmitting ? "running" : textResult ? "rendered" : "ready"}
            />
            <p style={{ margin: "0.7rem 0 0", lineHeight: 1.6, color: "#4d453d" }}>
              Internal playbook preview only. No live messaging or clinic deployment.
            </p>
          </StudioPreviewCard>

          <StudioPreviewCard title="Voice readiness" eyebrow="Current state">
            <StudioPreviewStatusPill
              status={
                loading ? "loading" : accessBlocked ? "loading" : voiceLane?.ready ? "ready" : "blocked"
              }
              label={
                loading
                  ? "loading"
                  : accessBlocked
                    ? "awaiting operator access"
                    : voiceLane?.ready
                      ? "preview ready"
                      : "preview blocked"
              }
            />
            <p style={{ margin: "0.7rem 0 0", lineHeight: 1.6, color: "#4d453d" }}>
              {accessBlocked
                ? `Internal preview voice route becomes available after operator access is confirmed. Route: ${voiceLane?.executionPath || "/api/internal/voice/preview/render"}`
                : `Execution path: ${voiceLane?.executionPath || "/api/internal/voice/preview/render"}`}
            </p>
          </StudioPreviewCard>

          <StudioPreviewCard title="Launch control" eyebrow="Live-use guidance">
            <StudioPreviewStatusPill
              status={accessBlocked ? "loading" : launchControlTone(launchControl)}
              label={
                accessBlocked
                  ? "review after access"
                  : launchControl?.overallReady
                    ? "review path clear"
                    : "live use blocked"
              }
            />
            <p style={{ margin: "0.7rem 0 0", lineHeight: 1.6, color: "#4d453d" }}>
              {accessBlocked
                ? "Launch guidance appears after preview access is confirmed. Nothing here activates live use."
                : launchControl?.overallReady
                  ? "No current launch-control blockers are reported here, but this page remains preview-only."
                  : `Live use is still blocked. Items to review: ${launchControl?.blockerCount ?? "unknown"}.`}
            </p>
          </StudioPreviewCard>

          <StudioPreviewCard title="Persona and voice" eyebrow="Safe selection">
            <StudioPreviewStatusPill
              status={accessBlocked ? "loading" : voiceLane?.personaSelection.voiceIdConfigured ? "ready" : "blocked"}
              label={selectedApprovedVoice.label}
            />
            <p style={{ margin: "0.7rem 0 0", lineHeight: 1.6, color: "#4d453d" }}>
              {accessBlocked
                ? `Draft selection: ${selectedApprovedVoice.label}. Preview runtime appears after operator access is confirmed. Intended live voice when readiness is healthy: ${currentRuntimeDefaultVoice.label}.`
                : `Selected in this draft: ${selectedApprovedVoice.label}. Preview runtime: ${activeRuntimeVoiceLabel(voiceLane)}. Intended live inbound default: ${currentRuntimeDefaultVoice.label}.`}
            </p>
            <p style={{ margin: "0.55rem 0 0", lineHeight: 1.6, color: "#5a524b" }}>
              {accessBlocked
                ? `This card shows the current voice plan, not a live switch. After preview access is confirmed, Preview shows the runtime truth. If readiness regresses or ElevenLabs is unavailable, live inbound returns to Twilio / Polly.`
                : `${bindingResolutionLabel(voiceLane)}. Preview shows runtime truth. Current live posture: ${voiceQualityReadiness?.deliveryPostureLabel || "Unavailable"}. When voice-quality readiness stays healthy, live inbound can use Erin through the ElevenLabs bridge. If readiness regresses or ElevenLabs is unavailable, live inbound returns to Twilio / Polly.`}
            </p>
          </StudioPreviewCard>

          <StudioPreviewCard title="Evidence ledger" eyebrow="Local history">
            <StudioPreviewStatusPill
              status={latestEvidence ? "rendered" : "ready"}
              label={latestEvidence ? "evidence recorded" : "ready for first proof"}
            />
            <p style={{ margin: "0.7rem 0 0", lineHeight: 1.6, color: "#4d453d" }}>
              {latestEvidence
                ? `${evidenceEntries.length} recorded preview item${evidenceEntries.length === 1 ? "" : "s"}. ${formatEvidenceTimestamp(latestEvidence.recordedAt || null)}`
                : "No preview evidence recorded yet. Save your first preview result here after operator access is confirmed."}
            </p>
          </StudioPreviewCard>
        </section>

        {accessBlocked ? (
          <section style={{ marginTop: "1.25rem" }}>
            <StudioPreviewCard title="Preview access required" eyebrow="Internal-only route">
              <StudioPreviewStatusPill status="blocked" label="operator token required" />
              <p style={{ margin: "0.8rem 0 0", lineHeight: 1.7, color: "#5a4949" }}>
                Add a valid operator or admin token to open internal preview routes and collect rehearsal evidence.
                Until access is confirmed, keep draft work in Studio and use the Ops Dashboard for live read-only checks.
              </p>
            </StudioPreviewCard>
          </section>
        ) : (
          <section
            style={{
              marginTop: "1.25rem",
              display: "grid",
              gridTemplateColumns: "minmax(0, 1.35fr) minmax(320px, 0.95fr)",
              gap: "1rem",
              alignItems: "start",
            }}
          >
            <div style={{ display: "grid", gap: "1rem" }}>
              <ScenarioRunner
                voiceLane={voiceLane}
                accessBlocked={accessBlocked}
                initialPresetId={requestedScenarioId || undefined}
                selectedStudioPersonaId={selectedStudioPersonaId}
                onEvidenceRecorded={(entry) =>
                  setEvidenceEntries((current) => [entry, ...current].slice(0, 24))
                }
              />

              <StudioPreviewCard title="Text preview" eyebrow="Playbook reply">
                <form onSubmit={handleTextPreview}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "180px minmax(0, 1fr)",
                      gap: "0.9rem",
                    }}
                  >
                    <label style={{ display: "grid", gap: "0.4rem" }}>
                      <span style={{ fontSize: "0.82rem", fontWeight: 700, color: "#5a524b" }}>
                        Channel
                      </span>
                      <select
                        aria-label="Studio text preview channel"
                        value={textChannel}
                        onChange={(event) =>
                          setTextChannel(event.target.value as StudioTextPreviewChannel)
                        }
                        style={{
                          borderRadius: 14,
                          border: "1px solid rgba(54, 44, 35, 0.12)",
                          padding: "0.7rem 0.8rem",
                          font: "inherit",
                          background: "#fffdfa",
                        }}
                      >
                        {textChannels.map((channel) => (
                          <option key={channel} value={channel}>
                            {channel}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label style={{ display: "grid", gap: "0.4rem" }}>
                      <span style={{ fontSize: "0.82rem", fontWeight: 700, color: "#5a524b" }}>
                        Message
                      </span>
                      <textarea
                        aria-label="Studio text preview message"
                        value={textMessage}
                        onChange={(event) => setTextMessage(event.target.value)}
                        rows={4}
                        style={{
                          width: "100%",
                          boxSizing: "border-box",
                          borderRadius: 16,
                          border: "1px solid rgba(54, 44, 35, 0.12)",
                          padding: "0.8rem 0.9rem",
                          font: "inherit",
                          background: "#fffdfa",
                          resize: "vertical",
                        }}
                      />
                    </label>
                  </div>
                  <div style={{ marginTop: "0.9rem", display: "flex", gap: "0.75rem", alignItems: "center", flexWrap: "wrap" }}>
                    <button
                      type="submit"
                      disabled={textSubmitting}
                      style={{
                        borderRadius: 999,
                        padding: "0.78rem 1.15rem",
                        border: "1px solid rgba(41, 84, 103, 0.18)",
                        background: "linear-gradient(135deg, #173847, #285466)",
                        color: "#f7f4ee",
                        fontWeight: 700,
                        cursor: textSubmitting ? "wait" : "pointer",
                      }}
                    >
                      {textSubmitting ? "Running..." : "Run text preview"}
                    </button>
                    {textError ? (
                      <span style={{ color: "#8a3a3a", fontWeight: 600 }}>{textError}</span>
                    ) : null}
                  </div>
                </form>

                {textResult ? (
                  <div
                    style={{
                      marginTop: "1rem",
                      borderRadius: 20,
                      border: "1px solid rgba(41, 84, 103, 0.12)",
                      background:
                        "linear-gradient(180deg, rgba(240, 248, 250, 0.92), rgba(252, 254, 255, 0.98))",
                      padding: "1rem",
                    }}
                  >
                    <div style={{ display: "flex", gap: "0.65rem", flexWrap: "wrap" }}>
                      <StudioPreviewStatusPill
                        status={textResult.matched ? "rendered" : "blocked"}
                        label={textResult.matched ? "matched" : "no match"}
                      />
                      {textResult.key ? (
                        <span style={{ color: "#496570", fontWeight: 600 }}>
                          key: {textResult.key}
                        </span>
                      ) : null}
                      {textResult.intent ? (
                        <span style={{ color: "#496570", fontWeight: 600 }}>
                          intent: {textResult.intent}
                        </span>
                      ) : null}
                    </div>
                    {textResult.reply ? (
                      <p
                        style={{
                          margin: "0.85rem 0 0",
                          color: "#314854",
                          lineHeight: 1.7,
                        }}
                      >
                        {textResult.reply}
                      </p>
                    ) : (
                      <p
                        style={{
                          margin: "0.85rem 0 0",
                          color: "#5e564f",
                          lineHeight: 1.7,
                        }}
                      >
                        No preview reply matched the current request.
                      </p>
                    )}
                  </div>
                ) : null}
              </StudioPreviewCard>

              <StudioPreviewCard title="Voice readiness" eyebrow="ElevenLabs preview lane">
                <div style={{ display: "flex", gap: "0.65rem", flexWrap: "wrap" }}>
                  <StudioPreviewStatusPill
                    status={voiceReady ? "ready" : "blocked"}
                    label={voiceReady ? "preview-ready" : "preview-blocked"}
                  />
                  <StudioPreviewStatusPill
                    status={launchControlTone(launchControl)}
                    label={
                      launchControl?.overallReady
                        ? "review path clear"
                        : "live use blocked"
                    }
                  />
                </div>
                <p style={{ margin: "0.85rem 0 0", lineHeight: 1.65, color: "#4d453d" }}>
                  Preview helps rehearse voice changes safely before anything in live
                  calling changes. Studio shows the current preview path, allowed
                  scenario channels, and the draft-selected versus active runtime
                  voice path. The approved catalog choice is saved in the Studio
                  draft and handoff package, while live calling stays on its current
                  safe delivery posture until an operator applies a later approved
                  change outside Studio. If ElevenLabs is unavailable during live
                  inbound, Twilio / Polly remains the fallback.
                </p>
                <details
                  style={{
                    marginTop: "0.8rem",
                    borderRadius: 18,
                    border: "1px solid rgba(54, 44, 35, 0.08)",
                    background: "#fbf8f4",
                    padding: "0.85rem 0.95rem",
                  }}
                >
                  <summary
                    style={{
                      cursor: "pointer",
                      listStyle: "none",
                      fontWeight: 700,
                      color: "#39535f",
                    }}
                  >
                    Install-day voice notes
                  </summary>
                  <p style={{ margin: "0.7rem 0 0", lineHeight: 1.65, color: "#5a524b" }}>
                    Install-day voice order: review voice-quality readiness, run one
                    supervised inbound smoke, confirm stable caller audio and the
                    correct handler path, then use `/metrics` last for live proof.
                  </p>
                  <p style={{ margin: "0.7rem 0 0", lineHeight: 1.65, color: "#5a524b" }}>
                    Keep Erin live only when readiness stays healthy, caller audio is
                    stable, the correct handler path is reached, and no fallback is
                    reported.
                  </p>
                  <p style={{ margin: "0.7rem 0 0", lineHeight: 1.65, color: "#5a524b" }}>
                    Install-day rollback cue: if live caller audio sounds wrong or
                    voice readiness regresses, return live inbound to Twilio / Polly
                    first, then follow the rollback plan before more testing.
                  </p>
                  <p style={{ margin: "0.7rem 0 0", lineHeight: 1.65, color: "#5a524b" }}>
                    First-week stabilization cue: once cutover is complete, treat
                    `/metrics` and the week-one checklist as the live operating path.
                    Return here only when reviewing a staged voice change or
                    collecting preview evidence. Midday review belongs here only if
                    fallback or caller-audio concerns still remain after the opening
                    pass.
                  </p>
                  <p style={{ margin: "0.7rem 0 0", lineHeight: 1.65, color: "#5a524b" }}>
                    Install-week proof note: after any supervised real call or
                    fallback event, record the handler path reached, whether caller
                    audio stayed stable, whether fallback appeared, and who owns the
                    next action before collecting more proof. Pair that note with a
                    current `/metrics` snapshot, not Preview alone.
                  </p>
                </details>
                <div
                  style={{
                    marginTop: "1rem",
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: "0.75rem",
                  }}
                >
                  {detailLabel(
                    "Execution path",
                    voiceLane?.executionPath || "/api/internal/voice/preview/render"
                  )}
                  {detailLabel(
                    "Allowed scenario channels",
                    (voiceLane?.allowedChannels || []).join(", ") || "Unavailable"
                  )}
                  {detailLabel(
                    "Blocked live channels",
                    (voiceLane?.blockedLaunchChannels || []).join(", ") || "Unavailable"
                  )}
                </div>
                {previewBlockers.length > 0 ? (
                  <ul
                    style={{
                      margin: "0.95rem 0 0",
                      paddingLeft: "1.1rem",
                      color: "#6b5555",
                      lineHeight: 1.7,
                    }}
                  >
                    {previewBlockers.map((reason) => (
                      <li key={reason}>{reason}</li>
                    ))}
                  </ul>
                ) : (
                  <p style={{ margin: "0.9rem 0 0", color: "#4d453d", lineHeight: 1.6 }}>
                    Voice preview readiness is clear for the currently allowed scenario
                    channels.
                  </p>
                )}
              </StudioPreviewCard>
            </div>

            <div style={{ display: "grid", gap: "1rem", alignContent: "start" }}>
              <StudioPreviewCard
                title="Draft-selected and active preview voice"
                eyebrow="Preview runtime contract"
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: "0.75rem",
                  }}
                >
                  {detailLabel("Draft source", "Local Studio workspace")}
                  {detailLabel("Draft persona", selectedStudioPersona.label)}
                  {detailLabel("Draft persona key", selectedStudioPersona.id)}
                  {detailLabel("Selected draft voice", selectedApprovedVoice.label)}
                  {detailLabel(
                    "Intended live inbound default",
                    currentRuntimeDefaultVoice.label
                  )}
                  {detailLabel(
                    "This selection affects",
                    selectedApprovedVoice.selectionScopeLabel
                  )}
                  {detailLabel(
                    "Active runtime persona",
                    voiceLane?.personaSelection.personaName || "Unavailable"
                  )}
                  {detailLabel(
                    "Active runtime key",
                    voiceLane?.personaSelection.personaKey || "Unavailable"
                  )}
                  {detailLabel(
                    "Active runtime voice",
                    activeRuntimeVoiceLabel(voiceLane)
                  )}
                  {detailLabel("Live fallback", "Twilio / Polly")}
                  {detailLabel(
                    "Binding path",
                    bindingResolutionLabel(voiceLane)
                  )}
                  {detailLabel("Voice source", voiceSourceLabel(voiceLane))}
                  {detailLabel(
                    "Model state",
                    voiceLane?.personaSelection.modelIdConfigured
                      ? "Configured via env"
                      : "Missing"
                  )}
                  {detailLabel(
                    "Catalog entry",
                    voiceLane?.personaSelection.catalogEntryLabel || "None"
                  )}
                  {detailLabel(
                    "Current scenario channels",
                    (voiceLane?.allowedChannels || []).join(", ") || "Unavailable"
                  )}
                </div>
                <p style={{ margin: "0.9rem 0 0", color: "#4d453d", lineHeight: 1.6 }}>
                  This card reports the current preview-only contract. The Studio
                  draft persona and approved voice come from local workspace
                  state, while the active rendered voice reflects either a
                  supported preview catalog entry or the safe server preview env
                  fallback. {selectedApprovedVoice.selectionScopeNote}
                </p>
              </StudioPreviewCard>

              <StudioPreviewCard
                title="Voice quality proof"
                eyebrow="Preview-safe proof lane"
              >
                <div style={{ display: "flex", gap: "0.65rem", flexWrap: "wrap" }}>
                  <StudioPreviewStatusPill
                    status={voiceReady ? "ready" : "blocked"}
                    label={voiceReady ? "preview quality visible" : "preview quality blocked"}
                  />
                  <StudioPreviewStatusPill
                    status={
                      voiceQualityReadiness?.deliveryPosture === "erin_live"
                        ? "ready"
                        : "blocked"
                    }
                    label={
                      voiceQualityReadiness?.deliveryPosture === "erin_live"
                        ? "erin-live ready"
                        : "twilio/polly contained"
                    }
                  />
                </div>
                <p style={{ margin: "0.85rem 0 0", color: "#4d453d", lineHeight: 1.6 }}>
                  This lane packages the existing preview and humanization proof into a
                  repeatable operator surface. It stays preview-safe and does not widen
                  Twilio or live clinic routing.
                </p>
                <div
                  style={{
                    marginTop: "1rem",
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: "0.75rem",
                  }}
                >
                  {detailLabel("Scoring harness", "Deterministic six-dimension QA")}
                  {detailLabel(
                    "Preview proof channels",
                    (voiceLane?.allowedChannels || []).join(", ") || "Unavailable"
                  )}
                  {detailLabel(
                    "Runtime boundary",
                    "Preview-only; Twilio production untouched"
                  )}
                  {detailLabel("Active runtime voice", activeRuntimeVoiceLabel(voiceLane))}
                  {detailLabel(
                    "Live delivery posture",
                    voiceQualityReadiness?.deliveryPostureLabel || "Unavailable"
                  )}
                  {detailLabel(
                    "Live transport",
                    voiceQualityReadiness?.liveTransport || "Unavailable"
                  )}
                  {detailLabel(
                    "Fallback reason",
                    voiceFallbackReasonLabel(voiceQualityReadiness)
                  )}
                </div>
                <p style={{ margin: "0.9rem 0 0", color: "#4d453d", lineHeight: 1.6 }}>
                  Use this lane to verify draft-selected voice, active preview runtime binding,
                  and conversation-quality readiness without implying live activation authority.
                </p>
                {stateErrors.voiceQualityReadiness ? (
                  <p style={{ margin: "0.8rem 0 0", color: "#8a3a3a", lineHeight: 1.6 }}>
                    {stateErrors.voiceQualityReadiness}
                  </p>
                ) : voiceQualityReadiness?.operatorCue ? (
                  <p style={{ margin: "0.8rem 0 0", color: "#5a524b", lineHeight: 1.6 }}>
                    Operator cue: {voiceQualityReadiness.operatorCue}
                  </p>
                ) : null}
              </StudioPreviewCard>

              <StudioPreviewCard title="Readiness blockers" eyebrow="Preview-only posture">
                {stateErrors.voiceReadiness ? (
                  <p style={{ margin: 0, color: "#8a3a3a", lineHeight: 1.6 }}>
                    {stateErrors.voiceReadiness}
                  </p>
                ) : (
                  <>
                    <div style={{ display: "flex", gap: "0.65rem", flexWrap: "wrap" }}>
                      <StudioPreviewStatusPill
                        status={voiceLane?.ready ? "ready" : "blocked"}
                        label={voiceLane?.ready ? "voice lane ready" : "voice lane blocked"}
                      />
                    </div>
                    {previewBlockers.length > 0 ? (
                      <ul
                        style={{
                          margin: "0.85rem 0 0",
                          paddingLeft: "1.1rem",
                          color: "#6b5555",
                          lineHeight: 1.7,
                        }}
                      >
                        {previewBlockers.map((reason) => (
                          <li key={reason}>{reason}</li>
                        ))}
                      </ul>
                    ) : (
                      <p style={{ margin: "0.8rem 0 0", color: "#4d453d", lineHeight: 1.6 }}>
                        No preview-lane blockers are currently reported.
                      </p>
                    )}
                  </>
                )}

                <div
                  style={{
                    marginTop: "1rem",
                    paddingTop: "1rem",
                    borderTop: "1px solid rgba(54, 44, 35, 0.08)",
                  }}
                >
                  {stateErrors.deploymentTruth ? (
                    <p style={{ margin: 0, color: "#8a3a3a", lineHeight: 1.6 }}>
                      {stateErrors.deploymentTruth}
                    </p>
                  ) : (
                    <>
                      <div style={{ display: "flex", gap: "0.65rem", flexWrap: "wrap" }}>
                        <StudioPreviewStatusPill
                          status={launchControlTone(launchControl)}
                          label={
                            launchControl?.overallReady
                              ? "launch control ready"
                              : "live use blocked"
                          }
                        />
                      </div>
                      <p style={{ margin: "0.8rem 0 0", color: "#4d453d", lineHeight: 1.6 }}>
                        Launch control blockers remain visible here so Studio never
                        hides the current deployment truth.
                      </p>
                      {launchNextActions.length > 0 ? (
                        <ul
                          style={{
                            margin: "0.8rem 0 0",
                            paddingLeft: "1.1rem",
                            color: "#5b534b",
                            lineHeight: 1.7,
                          }}
                        >
                          {launchNextActions.map((action) => (
                            <li key={action}>{action}</li>
                          ))}
                        </ul>
                      ) : null}
                    </>
                  )}
                </div>
              </StudioPreviewCard>

              <StudioPreviewCard title="Isolation guarantees" eyebrow="Live boundaries">
                <ul
                  style={{
                    margin: 0,
                    paddingLeft: "1.1rem",
                    color: "#4d453d",
                    lineHeight: 1.75,
                  }}
                >
                  <li>Live clinic deployment is blocked from this page.</li>
                  <li>Twilio production lanes remain untouched.</li>
                  <li>Voice rendering stays behind the current allowed scenario channels only.</li>
                  <li>Launch-control blockers remain visible and actionable.</li>
                </ul>
              </StudioPreviewCard>
            </div>
          </section>
        )}
      </div>
    </main>
  );
}
